<template>
  <!-- The navigation part of the sidebar (US 9372). Items and their module/permission rules are the same as
       in the old side menu; groups open and close and remember their state. -->
  <nav class="sb-nav" :class="{ 'sb-nav--rail': compact }" :aria-label="$t('app_nav.main_menu')">

    <!-- Main app -->
    <template v-if="getNavType === 'main'">
      <router-link class="sb-item"
                   to="/"
                   exact
                   qid="side-nav-home-link"
                   :aria-label="compact ? $t('system.home') : null"
      >
        <font-awesome-icon icon="home" class="sb-item__icon"/>
        <span class="sb-item__text">{{ $t('system.home') }}</span>
        <span class="sb-badge sb-badge--soft" v-if="taskCount && getClient.projectModule">{{ taskCount }}</span>
      </router-link>

      <div class="sb-group"
           v-for="group in visibleGroups"
           :key="group.key"
           :class="{ 'sb-group--active': isGroupActive(group) }"
           @mouseenter="placeFlyout"
           @focusin="placeFlyout"
      >
        <template v-if="group.children">
          <button type="button"
                  class="sb-item sb-item--group"
                  :class="{ 'sb-item--current': isGroupActive(group) && !isOpen(group) }"
                  :aria-expanded="isOpen(group) ? 'true' : 'false'"
                  @click="toggleGroup(group)"
                  :qid="group.qid"
          >
            <font-awesome-icon :icon="group.icon" class="sb-item__icon"/>
            <span class="sb-item__text">{{ $t(group.label) }}</span>
            <font-awesome-icon :icon="isOpen(group) ? 'angle-down' : 'angle-right'" class="sb-item__chevron"/>
          </button>
          <div class="sb-children" v-if="isOpen(group) && !compact">
            <router-link v-for="child in group.children"
                         :key="child.key"
                         class="sb-item sb-item--child"
                         :to="child.to"
                         :qid="child.qid"
            >
              <span class="sb-item__text">{{ $t(child.label) }}</span>
            </router-link>
          </div>
          <!-- Icon rail: the children show in a flyout on hover or focus -->
          <div class="sb-flyout" v-if="compact">
            <div class="sb-flyout__title">{{ $t(group.label) }}</div>
            <router-link v-for="child in group.children"
                         :key="'f-' + child.key"
                         class="sb-flyout__item"
                         :to="child.to"
            >
              {{ $t(child.label) }}
            </router-link>
          </div>
        </template>
        <router-link v-else
                     class="sb-item"
                     :to="group.to"
                     :qid="group.qid"
                     :aria-label="compact ? $t(group.label) : null"
        >
          <font-awesome-icon :icon="group.icon" class="sb-item__icon"/>
          <span class="sb-item__text">{{ $t(group.label) }}</span>
        </router-link>
      </div>
    </template>

    <!-- Maintenance and Administration: their own menu, with a way back -->
    <template v-else>
      <router-link class="sb-item sb-item--back"
                   to="/"
                   exact
                   qid="navbar-brand"
                   :aria-label="compact ? $t('app_nav.main_menu') : null"
      >
        <font-awesome-icon icon="angle-left" class="sb-item__icon"/>
        <span class="sb-item__text">{{ $t('app_nav.main_menu') }}</span>
      </router-link>
      <template v-for="section in areaSections">
        <!-- Icon rail: a section with several pages is one icon with its pages in a flyout, so the rail fits (US 9365) -->
        <div v-if="compact && section.label && section.items.length > 1"
             :key="'g-' + section.label"
             class="sb-group"
             :class="{ 'sb-group--active': isGroupActive({ children: section.items }) }"
             @mouseenter="placeFlyout"
             @focusin="placeFlyout"
        >
          <button type="button"
                  class="sb-item sb-item--group"
                  :class="{ 'sb-item--current': isGroupActive({ children: section.items }) }"
                  :aria-label="$t(section.label)"
                  :qid="'side-nav-section-' + (section.card || section.label)"
          >
            <font-awesome-icon :icon="sectionIcon(section)" class="sb-item__icon"/>
          </button>
          <div class="sb-flyout">
            <div class="sb-flyout__title">{{ $t(section.label) }}</div>
            <router-link v-for="item in section.items"
                         :key="'f-' + item.key"
                         class="sb-flyout__item"
                         :to="item.to"
                         :exact="!!item.exact"
                         :qid="item.qid"
            >
              {{ $t(item.label) }}
            </router-link>
          </div>
        </div>
        <template v-else>
          <div class="sb-section" :key="'s-' + section.label" v-if="section.label && !compact">{{ $t(section.label) }}</div>
          <router-link v-for="item in section.items"
                       :key="item.key"
                       class="sb-item"
                       :to="item.to"
                       :exact="!!item.exact"
                       :qid="item.qid"
                       :aria-label="compact ? $t(item.label) : null"
          >
            <font-awesome-icon :icon="item.icon" class="sb-item__icon"/>
            <span class="sb-item__text">{{ $t(item.label) }}</span>
          </router-link>
        </template>
      </template>
    </template>
  </nav>
</template>

<script>
import permissionMap from '@/util/ACL/permissionMap.js'
import permissionCheck from '@/util/ACL/permissionCheck';
import piincHttp from '@/util/configureAxios.js';
import { maintenanceSections, CARDS } from '@/util/maintenanceSettings.js';

const GROUPS_KEY = 'sidebarGroups';

function readGroups() {
  try {
    return JSON.parse(localStorage.getItem(GROUPS_KEY) || '{}') || {};
  } catch (e) {
    return {};
  }
}

export default {
  name: 'SidebarNav',
  props: {
    compact: {
      type: Boolean,
      default: false
    }
  },
  data: function() {
    return {
      navigation: {},
      user: {},
      taskCount: 0,
      openGroups: readGroups(),
    }
  },
  computed: {
    getNavType() {
      return this.$store.state.activeNavbar
    },
    getClient() {
      return this.$store.state.client
    },
    getUser() {
      return this.$store.state.user
    },
    getReloadCount() {
      return this.$store.state.reloadCount
    },
    // Main menu: same rules as the old side menu (desktop variant)
    groups() {
      let c = this.getClient;
      let n = this.navigation || {};
      let admin = this.isGranted(permissionMap.MAINTENANCE_VIEW);
      return [
        {
          key: 'dashboards', label: 'system.dashboard_title', icon: 'chart-bar', qid: 'side-nav-overview-link',
          children: [
            { key: 'regulatory', label: 'system.dashboard_regulatory_title', to: '/regulatory', qid: 'side-nav-compliance-dasboard-link', show: admin && c.advancedReportingModule },
            { key: 'tree', label: 'tree_view.title', to: '/tree-view', qid: 'side-nav-tree-view-link', show: c.gdprModule && (n.process || n.activity) },
            { key: 'validations', label: 'validations.title', to: '/validations', qid: 'side-nav-validations-link', show: c.gdprAdvancedModule && admin },
            { key: 'risk-matrix', label: 'risk_matrix.title', to: '/risk-matrix', qid: 'side-nav-risk-matrix-link', show: c.riskModule && admin },
          ]
        },
        {
          key: 'policies', label: 'maintenance.policies.title', icon: 'file-invoice', to: { name: 'policies' }, qid: 'policies-link',
          show: this.isGranted(permissionMap.POLICY_VIEW) && c.policyModule && ((this.user.organization && this.user.organization.length) || admin)
        },
        { key: 'processes', label: 'processes.title', icon: 'cube', to: '/processing-activities', qid: 'side-nav-processes-link', show: (n.process || n.activity) && c.processingActivitiesModule },
        { key: 'systems', label: 'systems.title', icon: 'desktop', to: '/systems', qid: 'side-nav-systems-link', show: n.system && c.systemModule },
        // One place for third parties and their contracts (US 9371)
        { key: 'contracts', label: 'maintenance.third_parties.vendors.title', icon: 'building', to: { name: 'contracts.third_parties' }, qid: 'side-nav-contracts-link', show: n.third_party && c.contractBasicModule },
        {
          key: 'risk', label: 'system.risk_management_title', icon: 'umbrella', qid: 'side-nav-risk-management-link',
          children: [
            { key: 'risk-scenarios', label: 'risk_scenarios.title', to: '/risk-scenarios', qid: 'side-nav-risk-scenarios-link', show: c.riskModule && n.risk_scenario },
            { key: 'generic-controls', label: 'generic_controls.title', to: { name: 'generic_controls' }, qid: 'side-nav-generic-controls-link', show: c.riskModule && n.generic_control },
          ]
        },
        // Project management is one page (US 9396), so a plain item instead of a group
        { key: 'project-management', label: 'projects.management.title', icon: 'calendar-alt', to: '/projects', qid: 'side-nav-project-management-link', show: c.projectModule && n.project },
        { key: 'incidents', label: 'projects.incident_management_list_title', icon: 'exclamation-triangle', to: '/incidents', qid: 'side-nav-incidents-link', show: c.incidentModule && n.project },
      ];
    },
    visibleGroups() {
      return this.groups
        .map(g => g.children ? Object.assign({}, g, { children: g.children.filter(child => child.show) }) : g)
        .filter(g => g.children ? g.children.length > 0 : g.show);
    },
    // Maintenance and Administration menus: same items and rules as the old light side menu
    areaSections() {
      let c = this.getClient;
      let can = p => this.isGranted(permissionMap[p]);
      let sections = this.getNavType === 'administration' ? [
        { label: null, items: [
          { key: 'adm-overview', label: 'admin_page.nav_overview', icon: 'th-large', to: { name: 'administration.dashboard' }, qid: 'administration-overview-link', exact: true, show: true },
        ]},
        { label: 'dashboard.administration', items: [
          { key: 'adm-clients', label: 'dashboard.clients', icon: 'layer-group', to: '/administration/clients', qid: 'administration-clients-link', show: true },
          { key: 'adm-users', label: 'dashboard.users', icon: 'user-cog', to: '/administration/users', qid: 'administration-users-link', show: true },
          { key: 'adm-guide', label: 'dashboard.guide', icon: 'book-open', to: '/administration/guide', qid: 'administration-guide-link', show: true },
          { key: 'adm-settings', label: 'dashboard.settings', icon: 'cogs', to: '/administration/settings', qid: 'administration-settings-link', show: true },
          { key: 'adm-features', label: 'administration.features.title', icon: 'tags', to: '/administration/features', qid: 'administration-features-link', show: true },
        ]},
      ] : null;
      if (!sections) {
        // Maintenance: the overview first, then the settings shared with the overview page
        return [{ label: null, items: [
          { key: 'overview', label: 'maintenance_overview.nav_item', icon: 'th-large', to: { name: 'maintenance.dashboard' }, qid: 'maintenance-overview-link', exact: true },
        ]}].concat(maintenanceSections(c, can));
      }
      return sections
        .map(s => Object.assign({}, s, { items: s.items.filter(i => i.show) }))
        .filter(s => s.items.length);
    }
  },
  watch: {
    getClient: function() {
      this.loadNavigation();
      this.loadCount();
    },
    getReloadCount: function() {
      this.loadCount();
    },
    // A group opens by itself when you are on one of its pages
    $route: function() {
      this.openActiveGroup();
    }
  },
  methods: {
    // The rail scrolls, so flyouts are fixed to the screen next to their icon instead of
    // hanging inside the menu, where the scroll area would cut them off (US 9365)
    placeFlyout(event) {
      let flyout = this.compact && event.currentTarget.querySelector('.sb-flyout');
      if (!flyout) {
        return;
      }
      let rect = event.currentTarget.getBoundingClientRect();
      flyout.style.left = (rect.left + 52) + 'px';
      flyout.style.top = rect.top + 'px';
      // Keep it on screen once it is shown and has a height
      window.requestAnimationFrame(function() {
        let overflow = flyout.getBoundingClientRect().bottom - (window.innerHeight - 8);
        if (overflow > 0) {
          flyout.style.top = Math.max(8, rect.top - overflow) + 'px';
        }
      });
    },
    isGranted(permission) {
      return permissionCheck(permission)
    },
    // The section's overview card icon; Administration has no card
    sectionIcon(section) {
      let card = CARDS.find(c => c.key === section.card);
      return card ? card.icon : 'user-shield';
    },
    isOpen(group) {
      return !!this.openGroups[group.key];
    },
    toggleGroup(group) {
      this.$set(this.openGroups, group.key, !this.isOpen(group));
      try {
        localStorage.setItem(GROUPS_KEY, JSON.stringify(this.openGroups));
      } catch (e) {
        // Remembering open groups is only a convenience
      }
    },
    childPath(child) {
      return this.$router.resolve(child.to).route.path;
    },
    isGroupActive(group) {
      let path = this.$route.path;
      return !!group.children && group.children.some(child => {
        let childPath = this.childPath(child);
        return path === childPath || path.indexOf(childPath + '/') === 0;
      });
    },
    openActiveGroup() {
      this.visibleGroups.forEach(group => {
        if (this.isGroupActive(group) && !this.isOpen(group)) {
          this.$set(this.openGroups, group.key, true);
        }
      });
    },
    loadNavigation() {
      let self = this;
      piincHttp.get('users/me/navigation', { params: {
        client: this.getClient.slug
      }}).then(function(response) {
        self.navigation = response.data;
        self.$nextTick(self.openActiveGroup);
      }, function() {});
    },
    loadUser() {
      let self = this;
      piincHttp.get('users/' + self.getUser.slug, { params: {
        client: this.getClient.slug
      }}).then(function(response) {
        self.user = response.data;
      }, function() {});
    },
    // Open tasks and rejected validations, shown on Home (as before)
    loadCount() {
      let self = this;
      if (!this.getClient.projectModule) {
        return;
      }
      piincHttp.get('projects/me/tasks', { params: {
        count: 1,
        status: 'pending',
        client: this.getClient.slug
      }}).then(function(response) {
        self.taskCount = response.data.total_count;
        piincHttp.get('validations/rejected', { params: {
          count: 1,
          client: self.getClient.slug
        }}).then(function(response) {
          self.taskCount = self.taskCount + response.data.total_count;
        }, function() {});
      }, function() {});
    }
  },
  created() {
    if (this.getClient && this.getClient.slug) {
      this.loadNavigation();
      this.loadCount();
      if (this.getUser && this.getUser.slug) {
        this.loadUser();
      }
    }
  }
}
</script>
